import React, { useEffect, useRef, useState } from "react";
import { Delete } from "lucide-react";

/**
 * Numeric PIN keypad + hidden input for keyboard support.
 * Fully controlled — parent owns the 4-digit state.
 */
export default function PinKeypad({
  value,
  onChange,
  onSubmit,
  disabled = false,
  autoFocus = true,
  testIdPrefix = "pin",
}) {
  const inputRef = useRef(null);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const push = (d) => {
    if (disabled) return;
    if (value.length >= 4) return;
    const next = (value + d).slice(0, 4);
    onChange(next);
    if (next.length === 4 && onSubmit) {
      // give the UI one tick to render the last dot
      setTimeout(() => onSubmit(next), 60);
    }
  };
  const backspace = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };
  const clear = () => {
    if (disabled) return;
    onChange("");
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      push(e.key);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      backspace();
    } else if (e.key === "Enter" && value.length === 4 && onSubmit) {
      e.preventDefault();
      onSubmit(value);
    }
  };

  // Visual dots
  const dots = [0, 1, 2, 3].map((i) => {
    const filled = value.length > i;
    return (
      <div
        key={i}
        data-testid={`${testIdPrefix}-dot-${i}`}
        className={
          "h-4 w-4 rounded-full transition-all duration-150 " +
          (filled ? "bg-neutral-900 scale-110" : "bg-neutral-200 border border-neutral-300")
        }
      />
    );
  });

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };
  // expose the shake helper through the ref-less imperative pattern used by parent via prop
  useEffect(() => {
    if (value.length === 0) return; // no shake at rest
  }, [value]);

  return (
    <div className="flex flex-col items-center gap-6 select-none">
      {/* Hidden capturing input (mobile + keyboard) */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={4}
        value={value}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
          onChange(digits);
          if (digits.length === 4 && onSubmit) setTimeout(() => onSubmit(digits), 60);
        }}
        onKeyDown={onKeyDown}
        className="sr-only"
        data-testid={`${testIdPrefix}-input`}
        aria-label="PIN"
      />

      <div className={"flex items-center gap-4 " + (shake ? "animate-shake" : "")}>
        {dots}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full max-w-[280px]">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => push(String(n))}
            disabled={disabled}
            data-testid={`${testIdPrefix}-key-${n}`}
            className="h-14 rounded-xl bg-white border border-neutral-200 text-lg font-semibold text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40 transition-colors"
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          data-testid={`${testIdPrefix}-key-clear`}
          className="h-14 rounded-xl bg-transparent text-xs font-medium text-neutral-500 hover:bg-neutral-100 disabled:opacity-40"
        >
          Borrar
        </button>
        <button
          type="button"
          onClick={() => push("0")}
          disabled={disabled}
          data-testid={`${testIdPrefix}-key-0`}
          className="h-14 rounded-xl bg-white border border-neutral-200 text-lg font-semibold text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40 transition-colors"
        >
          0
        </button>
        <button
          type="button"
          onClick={backspace}
          disabled={disabled}
          data-testid={`${testIdPrefix}-key-backspace`}
          className="h-14 rounded-xl bg-transparent text-neutral-500 hover:bg-neutral-100 disabled:opacity-40 flex items-center justify-center"
          aria-label="Retroceso"
        >
          <Delete className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
